'use client'
import { orderBy } from 'lodash'
import LocationHours from '@app/[locale]/(main)/locations/_components/location-hours'
import { useState } from 'react'
import type { TypePlacePublic } from '@lib/location-types'
import { Link, Modal, Typography } from '@shc/ui'
import NextLink from 'next/link'
import useAnalytics from '@hooks/use-analytics'

type LocationDepartmentsProps = {
  now: string
  departments: TypePlacePublic['contains_places']
}

const LocationDepartments = ({ now, departments }: LocationDepartmentsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalData, setModalData] = useState<
    Pick<TypePlacePublic, 'schedules' | 'schedule_notes' | 'schedule_exceptions'>
  >({})
  const { trackSnowplow } = useAnalytics()
  const sortDepartments = orderBy(departments, 'name_short')

  const onClickViewHoursButton = (
    data: Pick<TypePlacePublic, 'schedules' | 'schedule_notes' | 'schedule_exceptions'>
  ) => {
    setModalData(data)
    setIsModalOpen(true)
  }

  const handleOnClose = () => {
    setIsModalOpen(false)
    trackSnowplow({
      event: { name: 'modal_close', data: {} },
      contexts: [{ name: 'modal', data: { modal_name: 'DepartmentHoursModal' } }],
    })
  }

  return (
    <div>
      <Typography variant="h2" className="pb-6">
        Departments
      </Typography>
      <div>
        {sortDepartments.map(
          ({
            id,
            name_short,
            slug,
            phone,
            phone_formatted,
            fax_formatted,
            schedules,
            tags,
            schedule_notes,
            schedule_exceptions,
            secondary_address,
          }) => {
            const showHours = !!schedules?.length
            const showItem = tags?.some(
              (tag) => tag.name === 'Department' && tag.type === 'Category'
            )

            if (!showItem) return null

            return (
              <dl key={id} className="border-b">
                <div className="flex flex-row items-start gap-9 md:items-center py-4">
                  <dt className="basis-1/2 md:basis-5/12 text-gray-700 font-semibold">
                    {slug ? (
                      <Link as={NextLink} noUnderline href={slug}>
                        {name_short}
                      </Link>
                    ) : (
                      <Typography>{name_short}</Typography>
                    )}
                    {secondary_address && (
                      <Typography variant="body">{secondary_address}</Typography>
                    )}
                  </dt>
                  <dd className="basis-1/2 text-right md:basis-4/12 md:text-left">
                    <div className="empty:hidden">
                      {phone_formatted && (
                        <Link noUnderline href={`tel:+1${phone}`}>
                          {phone_formatted}
                        </Link>
                      )}
                      {fax_formatted && <Typography>Fax: {fax_formatted}</Typography>}
                      {showHours && (
                        <div className="md:hidden pt-3">
                          <Link
                            as="button"
                            noUnderline
                            onClick={() =>
                              onClickViewHoursButton({
                                schedules,
                                schedule_notes,
                                schedule_exceptions,
                              })
                            }>
                            View hours
                          </Link>
                        </div>
                      )}
                    </div>
                  </dd>
                  <dd className="hidden md:basis-3/12 md:block text-right">
                    {showHours && (
                      <Link
                        as="button"
                        noUnderline
                        onClick={() =>
                          onClickViewHoursButton({
                            schedules,
                            schedule_notes,
                            schedule_exceptions,
                          })
                        }>
                        View hours
                      </Link>
                    )}
                  </dd>
                </div>
              </dl>
            )
          }
        )}
      </div>
      <Modal width="sm" open={isModalOpen} onClose={handleOnClose}>
        <LocationHours
          now={now}
          schedule={modalData?.schedules}
          scheduleNotes={modalData?.schedule_notes}
          scheduleExceptions={modalData?.schedule_exceptions}
        />
      </Modal>
    </div>
  )
}

export default LocationDepartments
